/**
 * Governed weekly customer update — the PURE composer.
 *
 * `composeWeeklyUpdate(customer, data, now)` builds a per-customer update
 * email STRICTLY from real workspace data. Sections (each omitted entirely
 * when empty — no filler, ever):
 *
 *   (a) shipped this week — first-class deliverables that reached
 *       delivered/accepted within the trailing 7 days
 *   (b) closed — tickets resolved (done) within the trailing 7 days
 *   (c) in motion — in-progress deliverables/tasks that carry a due date
 *   (d) coming up — billing milestones / renewal dates within 30 days
 *   (e) one honest health line, REUSING the existing customer health
 *       scoring (`crm/health.ts`) — never a reimplementation
 *
 * Returns null when there is NOTHING real to report, so the caller skips
 * the customer instead of sending a padded email. Tone: warm, plain
 * English, zero hype — just what happened.
 *
 * Entity linkage uses only fields that actually exist on the records
 * (same discipline as `crm/weeklyUpdate.ts`):
 *   - Deliverable → `accountName` matches the customer's `company`.
 *   - ProjectTask → `personId` (exact) or `accountName` ↔ `company`.
 *   - Ticket carries no customer fields, so the only honest ties are a
 *     customer task bridging via `ticketId`, or a label carrying the
 *     customer's id, name, or company.
 *
 * Embedded `PersonDeliverable`s carry no timestamps, so they can never
 * honestly claim "shipped this week" — they only feed "in motion".
 *
 * PURE by contract: no store, no network, no writes, inputs untouched.
 */

import type { Deliverable, Person, ProjectTask, Ticket } from '../db/types';
import { scoreCustomerHealth } from '../crm/health';
import { escapeHtml } from '../crm/weeklyUpdate';

const DAY_MS = 86_400_000;
const WEEK_MS = 7 * DAY_MS;
const COMING_UP_DAYS = 30;

/** The live boards the composer reads. All optional — absent means empty. */
export interface WeeklyUpdateData {
  deliverables?: Deliverable[];
  tickets?: Ticket[];
  tasks?: ProjectTask[];
}

/** One line item in a section — a real record, nothing invented. */
export interface UpdateItem {
  title: string;
  detail?: string;
}

/** The assembled sections, before rendering. Empty arrays stay empty. */
export interface WeeklyUpdateSections {
  shipped: UpdateItem[];
  closed: UpdateItem[];
  inMotion: UpdateItem[];
  comingUp: UpdateItem[];
  /** One line from the existing health scoring; always derivable. */
  healthNote: string;
}

/** The rendered email. `text` is always present; `html` is Gmail-safe. */
export interface ComposedWeeklyUpdate {
  subject: string;
  text: string;
  html: string;
}

// --- helpers -----------------------------------------------------------------

function parseMs(iso: string | null | undefined): number | undefined {
  if (!iso) return undefined;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? undefined : t;
}

/** Case-insensitive, trimmed name equality. */
function sameName(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** Did this timestamp land within the trailing 7 days (and not the future)? */
function withinTrailingWeek(iso: string | null | undefined, now: number): boolean {
  const t = parseMs(iso);
  return t !== undefined && t <= now && now - t <= WEEK_MS;
}

/** Whole days until a (YYYY-MM-DD or ISO) date; negative once past. */
function daysUntil(iso: string | undefined, now: number): number | undefined {
  const t = parseMs(iso);
  if (t === undefined) return undefined;
  return Math.round((t - now) / DAY_MS);
}

function taskBelongsToCustomer(task: ProjectTask, customer: Person): boolean {
  if (task.personId) return task.personId === customer.id;
  return sameName(task.accountName, customer.company);
}

function deliverableBelongsToCustomer(d: Deliverable, customer: Person): boolean {
  return sameName(d.accountName, customer.company);
}

function ticketBelongsToCustomer(
  ticket: Ticket,
  customer: Person,
  customerTasks: ProjectTask[],
): boolean {
  if (customerTasks.some((t) => t.ticketId === ticket.id)) return true;
  return ticket.labels.some(
    (l) =>
      l === customer.id ||
      sameName(l, customer.name) ||
      sameName(l, customer.company),
  );
}

// --- recipient resolution ------------------------------------------------------

/** The resolved "send to" for a customer update. */
export interface CustomerUpdateRecipient {
  name: string;
  email: string;
}

function nonBlank(s: string | undefined): string | undefined {
  const t = s?.trim();
  return t ? t : undefined;
}

/**
 * Resolve who a weekly update for `customer` goes to. Primary POC first
 * (the flagged one, then any POC with an email), then the record's own
 * email. Returns null when nothing resolves — the caller surfaces the
 * honest "needs email" state, never a guessed address.
 */
export function resolveCustomerUpdateRecipient(
  customer: Person,
): CustomerUpdateRecipient | null {
  const pocs = customer.pocs ?? [];
  const poc =
    pocs.find((p) => p.primary && nonBlank(p.email)) ??
    pocs.find((p) => nonBlank(p.email));
  const pocEmail = nonBlank(poc?.email);
  if (poc && pocEmail) {
    return { name: nonBlank(poc.name) ?? pocEmail, email: pocEmail };
  }
  const ownEmail = nonBlank(customer.email);
  if (ownEmail) return { name: customer.name, email: ownEmail };
  return null;
}

// --- section assembly ------------------------------------------------------------

/**
 * Collect the sections for one customer from the live boards. PURE; each
 * bucket contains only that customer's real records, and stays an empty
 * array when there is genuinely nothing.
 */
export function collectWeeklyUpdateSections(
  customer: Person,
  data: WeeklyUpdateData,
  now: number = Date.now(),
): WeeklyUpdateSections {
  const deliverables = (data.deliverables ?? []).filter((d) =>
    deliverableBelongsToCustomer(d, customer),
  );
  const tasks = (data.tasks ?? []).filter((t) => taskBelongsToCustomer(t, customer));
  const tickets = (data.tickets ?? []).filter((t) =>
    ticketBelongsToCustomer(t, customer, tasks),
  );

  // (a) Shipped this week — deliverables completed in the trailing 7 days.
  const shipped: UpdateItem[] = deliverables
    .filter(
      (d) =>
        (d.status === 'delivered' || d.status === 'accepted') &&
        withinTrailingWeek(d.updatedAt, now),
    )
    .map((d) => ({
      title: d.name,
      detail: `completed ${d.updatedAt.slice(0, 10)}`,
    }));

  // (b) Closed — tickets resolved in the trailing 7 days.
  const closed: UpdateItem[] = tickets
    .filter((t) => t.status === 'done' && withinTrailingWeek(t.updatedAt, now))
    .map((t) => ({ title: t.title, detail: `closed ${t.updatedAt.slice(0, 10)}` }));

  // (c) In motion — in-progress deliverables/tasks that carry a due date.
  const inMotion: UpdateItem[] = [
    ...deliverables
      .filter((d) => d.status === 'in-progress' && d.dueDate)
      .map((d) => ({ title: d.name, detail: `due ${d.dueDate}` })),
    // Embedded checklist deliverables — no timestamps, so they only ever
    // appear here (never claimed as "shipped this week").
    ...(customer.deliverables ?? [])
      .filter((d) => d.status === 'in-progress' && d.dueDate)
      .map((d) => ({ title: d.title, detail: `due ${d.dueDate}` })),
    ...tasks
      .filter((t) => t.status === 'in-progress' && t.dueDate)
      .map((t) => ({ title: t.title, detail: `due ${t.dueDate}` })),
  ];

  // (d) Coming up — billing milestones / renewal within the next 30 days.
  const comingUp: UpdateItem[] = [];
  for (const bd of customer.billingDates ?? []) {
    if ((bd.status ?? 'scheduled') === 'paid') continue;
    const days = daysUntil(bd.date, now);
    if (days !== undefined && days >= 0 && days <= COMING_UP_DAYS) {
      comingUp.push({
        title: bd.label,
        detail: `due ${bd.date}${bd.amount != null ? ` · $${bd.amount.toLocaleString('en-US')}` : ''}`,
      });
    }
  }
  const renewalDays = daysUntil(customer.renewalDate, now);
  if (renewalDays !== undefined && renewalDays >= 0 && renewalDays <= COMING_UP_DAYS) {
    comingUp.push({ title: 'Contract renewal', detail: `on ${customer.renewalDate}` });
  }

  // (e) One-line health note — reuses the existing scoring, never parallel math.
  const health = scoreCustomerHealth(customer, { tasks: data.tasks ?? [], now });
  const bandText =
    health.band === 'at-risk' ? 'at risk' : health.band === 'watch' ? 'watch' : 'healthy';
  const worst = [...health.factors]
    .filter((f) => f.points < 0)
    .sort((a, b) => a.points - b.points)[0];
  const healthNote = `Account health: ${health.score}/100 (${bandText})${
    worst ? ` — biggest factor: ${worst.label.toLowerCase()}` : ''
  }.`;

  return { shipped, closed, inMotion, comingUp, healthNote };
}

// --- rendering -------------------------------------------------------------------

/** Section order + headings, shared by the text and HTML renderers. */
export const SECTION_ORDER: {
  key: keyof Pick<WeeklyUpdateSections, 'shipped' | 'closed' | 'inMotion' | 'comingUp'>;
  heading: string;
}[] = [
  { key: 'shipped', heading: 'Shipped this week' },
  { key: 'closed', heading: 'Closed' },
  { key: 'inMotion', heading: 'In motion' },
  { key: 'comingUp', heading: 'Coming up' },
];

/**
 * Compose the weekly update email for one customer. Returns null when
 * every section is empty — nothing real to report means NO email, not a
 * padded one. The health note alone never justifies a send: it is derived
 * commentary, not news.
 */
export function composeWeeklyUpdate(
  customer: Person,
  data: WeeklyUpdateData,
  now: number = Date.now(),
  ourCompany = 'Argo',
): ComposedWeeklyUpdate | null {
  const sections = collectWeeklyUpdateSections(customer, data, now);
  const { shipped, closed, inMotion, comingUp, healthNote } = sections;

  const moved = shipped.length + closed.length + inMotion.length;
  if (moved + comingUp.length === 0) return null;

  const subject =
    moved > 0
      ? `Your weekly update from ${ourCompany} — ${moved} thing${moved === 1 ? '' : 's'} moved this week`
      : `Your weekly update from ${ourCompany} — what's coming up`;

  const recipient = resolveCustomerUpdateRecipient(customer);
  const firstName = (recipient?.name ?? customer.name).trim().split(/\s+/)[0];

  // --- plain text (always present) ---
  const lines: string[] = [];
  lines.push(`Hi ${firstName},`);
  lines.push('');
  lines.push("Here's where things stand on your account this week.");
  for (const { key, heading } of SECTION_ORDER) {
    const items = sections[key];
    if (items.length === 0) continue; // omitted entirely — never padded
    lines.push('');
    lines.push(`${heading}:`);
    for (const item of items) {
      lines.push(`  - ${item.title}${item.detail ? ` (${item.detail})` : ''}`);
    }
  }
  lines.push('');
  lines.push(healthNote);
  lines.push('');
  lines.push('Anything look off? Just reply to this email.');
  lines.push(`— ${ourCompany}`);
  const text = lines.join('\n');

  // --- HTML (minimal, inline styles, Gmail-readable) ---
  const htmlParts: string[] = [];
  htmlParts.push(
    `<div style="font-family:-apple-system,'Segoe UI',Helvetica,Arial,sans-serif;max-width:600px;margin:0 auto;padding:24px;color:#1a1a1a;background:#ffffff;line-height:1.5;">`,
  );
  htmlParts.push(`<p style="margin:0 0 12px;">Hi ${escapeHtml(firstName)},</p>`);
  htmlParts.push(
    `<p style="margin:0 0 4px;">Here&#39;s where things stand on your account this week.</p>`,
  );
  for (const { key, heading } of SECTION_ORDER) {
    const items = sections[key];
    if (items.length === 0) continue;
    htmlParts.push(
      `<h3 style="margin:18px 0 6px;font-size:14px;color:#1a1a1a;">${escapeHtml(heading)}</h3>`,
    );
    htmlParts.push(`<ul style="margin:0;padding-left:20px;">`);
    for (const item of items) {
      htmlParts.push(
        `<li style="margin:3px 0;">${escapeHtml(item.title)}${
          item.detail
            ? ` <span style="color:#667085;">(${escapeHtml(item.detail)})</span>`
            : ''
        }</li>`,
      );
    }
    htmlParts.push(`</ul>`);
  }
  htmlParts.push(
    `<p style="margin:18px 0 0;color:#475467;">${escapeHtml(healthNote)}</p>`,
  );
  htmlParts.push(
    `<p style="margin:16px 0 0;">Anything look off? Just reply to this email.<br/>— ${escapeHtml(ourCompany)}</p>`,
  );
  htmlParts.push(`</div>`);
  const html = htmlParts.join('');

  return { subject, text, html };
}
